// import Vue from 'vue'
import { defHttp } from '../http/axios';
// import { v1 as uuidv1 } from 'uuid'
import { message } from 'ant-design-vue';
import { FileManager } from '@do/do-auth-web-client';

const fileExistanceUrl = `/doFileBackend/file/getFileBySHA1`;
const signatureUrl = `/doFileBackend/file/getSignature`;
const coverJsonUrl = `/doFileBackend/file/coverUploadJson`;
const saveFileUrl = `/doMistServer/projectResource/saveProjectResource`;
const insertFileUrl = `/doFileBackend/file/insertFile`;
const createPartTypeBatchUrl = `/doMistServer/mistProjectPartType/createPartTypeBatch`;

export interface IWebUploadSaveParams {
  fileAddress: string;
  fileSHA1: string;
  fileType: string;
  fileName: string;
  pid?: string;
  projectId?: number;
}
export interface IOSSFile {
  fileId: number;
  fileSize: number;
  fileSHA1: string;
  fileAddress: string;
  fileName: string;
  fileType: string;
}
export enum EN_BUSINESS_CODE {
  INVALID = '',
  // 项目文件
  PROJECT_FILE = 'project',
  // 组织文件
  GROUP_FILE = 'group',
  // 模型树文件
  MODEL_TREE_FILE = 'designFileSolutionFile',
}

export enum EN_RESOURCE_CODE {
  /** 零件 */
  PART = 'part',
  /** 零件库 */
  PARTLIBRARY = 'partLibrary',
  /** 项目资源 */
  PROJECTPART = 'projectPart',
  /** 项目资源库 */
  PROJECTPARTLIBRARY = 'projectPartLibrary',
  // 用户
  USER = 'user',
  // 文件根
  RPS = 'rps',
}

class UploadFileImpl {
  private _fileManager = new FileManager();
  private _init = false;

  constructor() {
    if (!this._init && window.ENV_CONFIG) {
      this._fileManager.init({
        env: window.ENV_CONFIG.env,
        ossUrl: window.ENV_CONFIG.loginUrl2,
        baseUrl: window.ENV_CONFIG.baseUrl + '/doFileBackend',
      });
      this._init = true;
    }
  }

  /**
   * @deprecated
   * 这个函数附带了太强的业务不通用
   * 请改用uploadFile2
   * @param file
   * @param resourceCode
   * @param projectId
   * @param pid
   * @returns
   */
  public async uploadFile(
    file: File,
    resourceCode: EN_RESOURCE_CODE = EN_RESOURCE_CODE.PROJECTPART,
    projectId?: number,
    pid?: string
  ): Promise<IOSSFile> {
    // 上传文件为项目文件必须添加项目id
    if (resourceCode === EN_RESOURCE_CODE.PROJECTPART && !projectId) {
      message.warn('项目文件缺失项目id');
      throw new Error('项目文件缺失项目id');
    }
    const ossFileInfo = await this.uploadFile2(file);
    if (resourceCode === EN_RESOURCE_CODE.USER) {
      return ossFileInfo;
    }
    const fileId = (await this._saveFile(
      resourceCode,
      ossFileInfo.fileAddress,
      ossFileInfo.fileSHA1,
      ossFileInfo.fileType,
      file.name,
      projectId,
      pid
    )) as number;
    ossFileInfo.fileId = fileId;
    return ossFileInfo;
  }

  public async uploadFile2(file: File): Promise<IOSSFile> {
    const { fileSize, fileSHA1, fileName, fileOssPath } = await this._fileManager.uploadFile(file);
    const fileTypeSplit = file.name.split('.');
    const fileType = fileTypeSplit.pop() ?? '';
    return {
      fileId: -1,
      fileSize,
      fileSHA1,
      fileAddress: fileOssPath,
      fileName,
      fileType,
    };
  }
  public async uploadObject(value: any, name: string): Promise<IOSSFile> {
    const file = new File([new Blob([JSON.stringify(value)])], name);
    const { fileSize, fileSHA1, fileName, fileOssPath } = await this._fileManager.uploadFile(file);
    const fileTypeSplit = file.name.split('.');
    const fileType = fileTypeSplit.pop() ?? '';
    return {
      fileId: -1,
      fileSize,
      fileSHA1,
      fileAddress: fileOssPath,
      fileName,
      fileType,
    };
  }
  public async coverUploadJson(
    fileAddressPath: string,
    jsonArray?: any[],
    json?: any
  ): Promise<void> {
    await defHttp.post({
      url: `${window.ENV_CONFIG.baseUrl}${coverJsonUrl}`,
      params: {
        fileAddressPath,
        jsonArray,
        json,
      },
    });
  }

  public async createPartTypeBatch(params: any): Promise<void> {
    await defHttp.post({
      url: `${createPartTypeBatchUrl}`,
      params,
    });
  }

  /** OSS 直传完成后登记文件缓存 */
  public async insertFile(sha1: string, path: string): Promise<void> {
    await defHttp.post({
      url: `${window.ENV_CONFIG.baseUrl}${insertFileUrl}`,
      params: { sha1, path },
    });
  }

  /** Web 上传数模：登记 OSS 文件并创建项目资源，返回 resourceId */
  public async saveWebUploadResource(params: IWebUploadSaveParams): Promise<string> {
    await this.insertFile(params.fileSHA1, params.fileAddress);
    const resourceId = await this._saveFile(
      EN_RESOURCE_CODE.PROJECTPART,
      params.fileAddress,
      params.fileSHA1,
      params.fileType,
      params.fileName,
      params.projectId,
      params.pid,
    );
    return String(resourceId);
  }

  private async _readSlice(file: File, start: number, size: number): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
      const fileReader = new FileReader();
      const slice = file.slice(start, start + size);

      fileReader.onload = () => resolve(new Uint8Array(fileReader.result as any));
      fileReader.onerror = reject;
      fileReader.readAsArrayBuffer(slice);
    });
  }

  // TODO: 目前另写了一套axios发送，后续axios封装统一后再改

  // 验证文件是否已存在
  private async _checkIfExist(fileSHA1: string) {
    const params = {
      fileSHA1,
    };

    const data = await defHttp.get({
      url: `${window.ENV_CONFIG.baseUrl}${fileExistanceUrl}`,
      params,
    });
    return data;
  }

  private async _getSignature(fileName: string, businessCode: EN_BUSINESS_CODE) {
    const params = {
      fileName,
      businessCode,
    };
    const data = await defHttp.get({ url: `${window.ENV_CONFIG.baseUrl}${signatureUrl}`, params });
    return data;
  }

  private async _getSignaturePath(fileOssPath: string, businessCode: EN_BUSINESS_CODE) {
    const params = {
      fileOssPath,
      businessCode,
    };
    const { data } = await defHttp.get({
      url: `${window.ENV_CONFIG.baseUrl}${signatureUrl}`,
      params,
    });
    return data;
  }

  private async _saveFile(
    resourceCode: EN_RESOURCE_CODE,
    fileAddress: string,
    sha1: string,
    fileType: string,
    fileName: string,
    projectId?: number,
    pid?: string
  ) {
    const properties = {
      projectPart_type: resourceCode === EN_RESOURCE_CODE.RPS ? 'rps' : 'part',
      projectPart_project_id: projectId,
      projectPart_is_root: true,
      projectPart_file_address: fileAddress,
      projectPart_file_type: fileType,
      projectPart_catia_file_sha1: sha1,
    };
    const params = {
      name: fileName,
      projectId,
      resourceCode,
      properties,
      type: 'file',
      pid,
    };

    const { data } = await defHttp.post({
      url: `${window.ENV_CONFIG.baseUrl}${saveFileUrl}`,
      params,
    });
    return data;
  }
}

export const UploadFileService = new UploadFileImpl();
